import {
  Alert, Box, Button, CircularProgress, IconButton, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'constants/theme'
import { useTranslation } from 'react-i18next'
import AboutUs from 'api/admin/clinic-manage/about.api'
import DashboardConfigsApi, { AgeGroupBucket } from 'api/executive/dashboard-configs.api'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextField from 'component/Input/InputTextField'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import { icons } from 'constants/images'
import InfoBanner from 'new-components/banners/info-banner'
import TabLoading from './TabLoading'

const DEFAULT_BUCKETS: AgeGroupBucket[] = [
  { label: '<18',  minAge: null, maxAge: 17 },
  { label: '18-30', minAge: 18,  maxAge: 30 },
  { label: '31-45', minAge: 31,  maxAge: 45 },
  { label: '46-60', minAge: 46,  maxAge: 60 },
  { label: '60+',  minAge: 61,  maxAge: null },
]

const bucketsEqual = (a: AgeGroupBucket[], b: AgeGroupBucket[]) => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].label !== b[i].label || a[i].minAge !== b[i].minAge || a[i].maxAge !== b[i].maxAge) return false
  }
  return true
}

/**
 * กลุ่มอายุ — admin-tunable demographic buckets used by the Insight
 * dashboard's age-group widget. Buckets render in the order shown
 * here; minAge/maxAge are inclusive; leaving either blank means an
 * open-ended bucket (the first usually has no minAge, the last has
 * no maxAge).
 */
export default function AgeGroupsTab() {
  const { t } = useTranslation()
  const [aboutUsId, setAboutUsId] = useState<number | null>(null)
  const [buckets, setBuckets] = useState<AgeGroupBucket[]>([])
  /** Snapshot taken at every successful load() and after a successful save.
   *  hasUnsavedChanges diffs the live `buckets` against this. */
  const [baseline, setBaseline] = useState<AgeGroupBucket[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isDefault, setIsDefault] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Resolve aboutUsId once. We surface failures here because the Save button
  // is gated on `aboutUsId != null` — if this silently returns the wrong
  // shape (or fails) the user sees a permanently-disabled Save with no
  // explanation.
  useEffect(() => {
    AboutUs.findAboutUs()
      .then((res) => {
        const id = Number((res?.data as { aboutUsId?: number })?.aboutUsId)
        if (Number.isFinite(id) && id > 0) {
          setAboutUsId(id)
        } else {
          // eslint-disable-next-line no-console
          console.warn('[AgeGroupsTab] AboutUs.findAboutUs returned no aboutUsId — response:', res?.data)
          notiError(t('DASHBOARD_TARGETS.AGE.NO_ABOUT_US', {
            defaultValue: 'โหลดข้อมูลคลินิกไม่ได้ — บันทึกชั่วคราวไม่สามารถใช้งานได้',
          }) as string)
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[AgeGroupsTab] AboutUs.findAboutUs failed:', e)
        notiError(t('DASHBOARD_TARGETS.AGE.ABOUT_US_ERROR', {
          defaultValue: 'โหลดข้อมูลคลินิกล้มเหลว — ลองรีโหลดหน้าเว็บ',
        }) as string)
      })
  }, [])

  // Load buckets when aboutUsId is available
  useEffect(() => {
    if (aboutUsId == null) return
    setLoading(true)
    DashboardConfigsApi.getAgeGroups(aboutUsId)
      .then((r) => {
        setBuckets(r.buckets)
        setBaseline(r.buckets)
        setIsDefault(r.isDefault)
      })
      .catch(() => {
        setBuckets(DEFAULT_BUCKETS)
        setBaseline(DEFAULT_BUCKETS)
      })
      .finally(() => setLoading(false))
  }, [aboutUsId])

  const update = (i: number, patch: Partial<AgeGroupBucket>) => {
    setBuckets((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))
  }

  const addRow = () => {
    setBuckets((prev) => [...prev, { label: '', minAge: null, maxAge: null }])
  }

  const removeRow = (i: number) => {
    setBuckets((prev) => prev.filter((_, idx) => idx !== i))
  }

  const resetToDefault = () => {
    setBuckets(DEFAULT_BUCKETS)
    setValidationError(null)
  }

  const hasUnsavedChanges = useMemo(() => !bucketsEqual(buckets, baseline), [buckets, baseline])

  /** Concrete reason the Save button is currently disabled — null if it's
   *  enabled. Exposed as a tooltip so the user can tell whether they're
   *  blocked by an environment issue (no aboutUsId), nothing-to-save state,
   *  or an in-flight request. */
  const saveDisabledReason: string | null = saving
    ? (t('DASHBOARD_TARGETS.AGE.SAVE_IN_FLIGHT', { defaultValue: 'กำลังบันทึก...' }) as string)
    : aboutUsId == null
      ? (t('DASHBOARD_TARGETS.AGE.NO_ABOUT_US_SHORT', { defaultValue: 'ยังโหลดข้อมูลคลินิกไม่สำเร็จ' }) as string)
      : !hasUnsavedChanges
        ? (t('DASHBOARD_TARGETS.AGE.NOTHING_TO_SAVE', { defaultValue: 'ยังไม่มีการเปลี่ยนแปลง' }) as string)
        : null

  const validate = (): string | null => {
    if (buckets.length === 0) return t('DASHBOARD_TARGETS.AGE.NEED_ONE', 'ต้องมีกลุ่มอย่างน้อย 1 รายการ') as string
    for (const [i, b] of buckets.entries()) {
      const row = i + 1
      if (!b.label || !b.label.trim()) return t('DASHBOARD_TARGETS.AGE.LABEL_REQUIRED', `แถวที่ ${row}: ต้องตั้งชื่อกลุ่ม`) as string
      if (b.minAge != null && (b.minAge < 0 || b.minAge > 150)) return `แถวที่ ${row}: อายุต่ำสุดต้องอยู่ระหว่าง 0-150`
      if (b.maxAge != null && (b.maxAge < 0 || b.maxAge > 150)) return `แถวที่ ${row}: อายุสูงสุดต้องอยู่ระหว่าง 0-150`
      if (b.minAge != null && b.maxAge != null && b.minAge > b.maxAge) return `แถวที่ ${row}: อายุต่ำสุด > อายุสูงสุด`
    }
    // Coverage + overlap: every integer age 0-150 must belong to exactly
    // ONE bucket. Gaps mean a real patient won't appear in any group on
    // the Insight dashboard; overlaps mean they get double-counted. Both
    // are reported as concrete inclusive ranges so the admin can target
    // the exact bucket to fix.
    const counts = new Array(151).fill(0)
    for (const b of buckets) {
      const lo = Math.max(0, b.minAge ?? 0)
      const hi = Math.min(150, b.maxAge ?? 150)
      for (let a = lo; a <= hi; a++) counts[a]++
    }
    const findRanges = (matches: (c: number) => boolean): Array<[number, number]> => {
      const ranges: Array<[number, number]> = []
      let start = -1
      for (let a = 0; a <= 150; a++) {
        if (matches(counts[a]) && start === -1) start = a
        if (!matches(counts[a]) && start !== -1) { ranges.push([start, a - 1]); start = -1 }
      }
      if (start !== -1) ranges.push([start, 150])
      return ranges
    }
    const fmt = (rs: Array<[number, number]>) =>
      rs.map(([s, e]) => (s === e ? `${s}` : `${s}-${e}`)).join(', ')

    const gaps = findRanges((c) => c === 0)
    if (gaps.length > 0) {
      return t('DASHBOARD_TARGETS.AGE.UNCOVERED', {
        defaultValue: 'อายุ {{ranges}} ไม่อยู่ในกลุ่มใดเลย — ขยายช่วงให้ครอบคลุม',
        ranges: fmt(gaps),
      }) as string
    }
    const overlaps = findRanges((c) => c >= 2)
    if (overlaps.length > 0) {
      return t('DASHBOARD_TARGETS.AGE.OVERLAP', {
        defaultValue: 'อายุ {{ranges}} ซ้อนทับกันหลายกลุ่ม — แก้ไขช่วงไม่ให้ซ้อน',
        ranges: fmt(overlaps),
      }) as string
    }
    return null
  }

  const save = async () => {
    if (aboutUsId == null) return
    const err = validate()
    if (err) {
      setValidationError(err)
      return
    }
    setValidationError(null)
    setSaving(true)
    try {
      await DashboardConfigsApi.saveAgeGroups(aboutUsId, buckets)
      setBaseline(buckets)
      setIsDefault(false)
      notiSuccess(t('DASHBOARD_TARGETS.SAVE_SUCCESS') as string)
    } catch (e) {
      console.log('e',e)
      const msg = (e as any)?.response?.data?.message
      notiError(typeof msg === 'string' ? msg : (t('DASHBOARD_TARGETS.SAVE_ERROR') as string))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <TabLoading />
  }

  return (
    <Box>
      <InfoBanner sx={{ mb: 2 }}>
        {t(
          'DASHBOARD_TARGETS.AGE.DESCRIPTION',
          'กำหนดช่วงอายุที่ใช้แสดงในแดชบอร์ด Insight → กลุ่มอายุ — ช่องที่ว่างจะแปลว่า "ไม่จำกัด" (เปิด)'
        )}
        {isDefault && (
          <> — {t('DASHBOARD_TARGETS.AGE.USING_DEFAULTS', 'กำลังใช้ค่าเริ่มต้น — กดบันทึกหลังแก้ไขเพื่อใช้ค่าของคลินิก')}</>
        )}
      </InfoBanner>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <ButtonCustom
            variant="outlined"
            onClick={resetToDefault}
            disabled={saving}
            textButton={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <RestartAltIcon fontSize="small" />
                {t('DASHBOARD_TARGETS.AGE.RESET_DEFAULT', 'คืนค่าเริ่มต้น')}
              </span>
            }
          />
          <Tooltip title={saveDisabledReason ?? ''} placement="top">
            <span>
              <ButtonCustom
                variant="contained"
                onClick={save}
                disabled={Boolean(saveDisabledReason)}
                textButton={saving ? <CircularProgress size={20} /> : t('DASHBOARD_TARGETS.SAVE')}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      <Paper sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }} align="center">#</TableCell>
              <TableCell>{t('DASHBOARD_TARGETS.AGE.LABEL', 'ชื่อกลุ่ม')}</TableCell>
              <TableCell sx={{ width: 140 }} align="right">{t('DASHBOARD_TARGETS.AGE.MIN', 'อายุต่ำสุด')}</TableCell>
              <TableCell sx={{ width: 140 }} align="right">{t('DASHBOARD_TARGETS.AGE.MAX', 'อายุสูงสุด')}</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Add-row affordance matches IMPORT_PRODUCT.FORM.ADD_NEW_PRODUCT pattern:
                a TableRow spanning the full width with a text-variant Button
                left-aligned at the top of the body. */}
            <TableRow>
              <TableCell
                colSpan={5}
                align="left"
                onClick={() => { if (!saving) addRow() }}
                sx={{ p: 1, cursor: saving ? 'default' : 'pointer' }}
              >
                <Button
                  variant="text"
                  disabled={saving}
                  sx={{
                    color: colors.themeSecondColor,
                    textTransform: 'none',
                    fontWeight: 500,
                    minWidth: 0,
                    p: 0,
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  <span style={{ marginRight: 4 }}>+</span>
                  {t('DASHBOARD_TARGETS.AGE.ADD_ROW', 'เพิ่มกลุ่ม')}
                </Button>
              </TableCell>
            </TableRow>
            {buckets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {t('DASHBOARD_TARGETS.AGE.EMPTY', 'ยังไม่มีกลุ่ม — กด "เพิ่มกลุ่ม"')}
                </TableCell>
              </TableRow>
            ) : (
              buckets.map((b, i) => (
                <TableRow key={i} hover>
                  <TableCell align="center">{i + 1}</TableCell>
                  <TableCell>
                    <InputTextField
                      hookValue
                      inputHeight={37}
                      placeholder="<18, เด็ก, ผู้สูงอายุ, ..."
                      value={b.label}
                      onchange={(e: any) => update(i, { label: e.target.value })}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <InputTextField
                      hookValue
                      inputHeight={37}
                      type="number"
                      inputTextAlign="right"
                      placeholder="—"
                      value={b.minAge ?? ''}
                      onchange={(e: any) =>
                        update(i, { minAge: e.target.value === '' ? null : Number(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 150 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <InputTextField
                      hookValue
                      inputHeight={37}
                      type="number"
                      inputTextAlign="right"
                      placeholder="—"
                      value={b.maxAge ?? ''}
                      onchange={(e: any) =>
                        update(i, { maxAge: e.target.value === '' ? null : Number(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 150 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('DASHBOARD_TARGETS.AGE.REMOVE', 'ลบ') as string}>
                      <IconButton size="small" onClick={() => removeRow(i)}>
                        <img src={icons.btnTrash} alt="delete" style={{ width: 20, height: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t(
            'DASHBOARD_TARGETS.AGE.HINT',
            'เคล็ดลับ: ปล่อย "อายุต่ำสุด" ว่างในแถวแรก และ "อายุสูงสุด" ว่างในแถวสุดท้าย เพื่อให้ครอบคลุมทุกคนไข้'
          )}
          {hasUnsavedChanges && ` · ${t('DASHBOARD_TARGETS.UNSAVED', 'มีการเปลี่ยนแปลงที่ยังไม่บันทึก')}`}
        </Typography>
      </Box>
    </Box>
  )
}
